const AUTHOR_ID_PATTERN = /^author_[0-9a-f]{32}$/;
const MAX_DISPLAY_NAME_SCALARS = 80;
const MAX_HANDLE_LENGTH = 32;

function unicodeScalarLength(value) {
  return Array.from(value).length;
}

function normalizedAuthorToken(authorId) {
  const prefixLength = "author_".length;
  return decodeAccountAuthorIdV1(authorId).slice(
    prefixLength,
    prefixLength + 8,
  );
}

export function decodeAccountAuthorIdV1(value) {
  if (typeof value !== "string" || !AUTHOR_ID_PATTERN.test(value)) {
    throw new TypeError("Account author ID is invalid.");
  }
  return value;
}

export function createAccountAuthorId(cryptoRef = globalThis.crypto) {
  let suffix;
  if (typeof cryptoRef?.randomUUID === "function") {
    suffix = cryptoRef.randomUUID().replaceAll("-", "").toLowerCase();
  } else if (typeof cryptoRef?.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    cryptoRef.getRandomValues(bytes);
    suffix = Array.from(
      bytes,
      (value) => value.toString(16).padStart(2, "0"),
    ).join("");
  } else {
    throw new TypeError("Secure author identity generation is unavailable.");
  }
  return decodeAccountAuthorIdV1(`author_${suffix}`);
}

export function canonicalAuthorshipProfileDisplayName(
  value,
  { allowEmpty = false } = {},
) {
  if (typeof value !== "string") {
    throw new TypeError("Authorship display name must be text.");
  }
  const displayName = value.trim();
  if (!allowEmpty && !displayName) {
    throw new TypeError("Authorship display name is required.");
  }
  if (unicodeScalarLength(displayName) > MAX_DISPLAY_NAME_SCALARS) {
    throw new RangeError(
      `Authorship display name must be at most ${MAX_DISPLAY_NAME_SCALARS} characters.`,
    );
  }
  return displayName;
}

export function decodeAuthorshipProfileDisplayNameV1(value) {
  const displayName = canonicalAuthorshipProfileDisplayName(value);
  if (displayName !== value) {
    throw new TypeError("Account authorship display name is not canonical.");
  }
  return displayName;
}

export function canonicalAuthorshipProfileHandle(
  value,
  { allowEmpty = false } = {},
) {
  if (typeof value !== "string") {
    throw new TypeError("Authorship handle must be text.");
  }
  const handle = value
    .trim()
    .replace(/^@+/, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_HANDLE_LENGTH)
    .replace(/-+$/g, "");
  if (!allowEmpty && !handle) {
    throw new TypeError(
      "Authorship handle must contain an ASCII letter or number.",
    );
  }
  return handle;
}

export function decodeAuthorshipProfileHandleV1(value) {
  const handle = canonicalAuthorshipProfileHandle(value);
  if (handle !== value) {
    throw new TypeError("Account authorship handle is not canonical.");
  }
  return handle;
}

export function accountAuthorshipProfileIdentityWithDeterministicDefaults(identity) {
  if (!identity || typeof identity !== "object" || Array.isArray(identity)) {
    throw new TypeError("Authorship identity must be an object.");
  }
  const authorId = decodeAccountAuthorIdV1(identity.authorId);
  const token = normalizedAuthorToken(authorId);
  const displayName = canonicalAuthorshipProfileDisplayName(
    identity.displayName ?? "",
    { allowEmpty: true },
  ) || `FishyStuff author ${token.toUpperCase()}`;
  const handle = canonicalAuthorshipProfileHandle(
    identity.handle ?? "",
    { allowEmpty: true },
  ) || `author-${token.toLowerCase()}`;
  return Object.freeze({ authorId, displayName, handle });
}

export function historicalAuthorshipProfileProjection(state) {
  if (state?.kind !== "ready") return null;
  return Object.freeze({
    id: state.authorId,
    displayName: state.displayName,
    handle: `@${state.handle}`,
  });
}
